import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { SectionHeading, Reveal } from "./Reveal";

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const [touched, setTouched] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  useEffect(() => {
    if (!inView || touched) return undefined;
    const controls = animate(50, [50, 28, 72, 50], { duration: 2.6, ease: "easeInOut", onUpdate: setPos });
    return () => controls.stop();
  }, [inView, touched]);

  const onChange = (e) => {
    setTouched(true);
    setPos(Number(e.target.value));
  };

  return (
    <section id="before-after" aria-labelledby="ba-heading" className="relative px-5 pb-24 sm:px-8 lg:pb-36">
      <div className="mx-auto max-w-7xl">
        <SectionHeading id="ba-heading" eyebrow="Before & after" title="Same table. Same memories. Less mess." intro="Drag the slider to clear the paper pile and keep what matters." />
        <Reveal delay={0.1}>
          <div ref={ref} data-testid="before-after-slider" className="relative mt-12 overflow-hidden rounded-[2rem] bg-navy shadow-[0_40px_90px_-40px_rgba(18,52,90,0.6)] lg:mt-16 select-none">
            <img src="/images/after.webp" alt="A clean, tidy kitchen table with a phone showing a neat grid of saved children's artwork" loading="lazy" width="1264" height="848" className="block aspect-[4/3] sm:aspect-[1264/848] w-full object-cover" />
            <img
              src="/images/before.webp"
              alt="The same kitchen table buried under a messy pile of children's drawings, paintings and crafts"
              loading="lazy"
              width="1264"
              height="848"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute left-4 top-4 rounded-full bg-cream/95 px-3.5 py-1.5 font-heading text-xs sm:text-sm font-extrabold text-navy shadow">Before<span className="hidden sm:inline">: the paper pile</span></span>
            <span className="absolute right-4 top-4 rounded-full bg-teal px-3.5 py-1.5 font-heading text-xs sm:text-sm font-extrabold text-white shadow">After<span className="hidden sm:inline">: kept with KeepThis!</span></span>
            <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-[3px] -translate-x-1/2 bg-cream shadow-[0_0_20px_rgba(0,0,0,0.35)]" style={{ left: `${pos}%` }}>
              <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream text-navy shadow-xl ring-4 ring-teal/40">
                <MoveHorizontal className="h-6 w-6" />
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.5"
              value={pos}
              onChange={onChange}
              aria-label="Drag to compare the messy paper pile with the tidy table"
              data-testid="before-after-range"
              className="ba-range absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
